import { useState, useRef, useEffect, KeyboardEvent } from 'react';

interface MessageInputProps {
  onSend: (text: string) => void;
  disabled?: boolean;
  voiceText?: string;
  onFocus?: () => void;
}

export default function MessageInput({ onSend, disabled = false, voiceText, onFocus }: MessageInputProps) {
  const [text, setText] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isComposingRef = useRef(false);

  useEffect(() => {
    if (voiceText !== undefined) {
      setText(voiceText);
    }
  }, [voiceText]);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const newHeight = Math.min(el.scrollHeight, 100);
    el.style.height = `${newHeight}px`;
  }, [text]);

  const handleSend = () => {
    if (text.trim() && !disabled) {
      onSend(text.trim());
      setText('');
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !isComposingRef.current) {
      e.preventDefault();
      handleSend();
    }
  };

  const canSend = text.trim().length > 0 && !disabled;

  return (
    <div className="flex items-end gap-1.5 w-full">
      {/* テキスト入力欄 */}
      <div className="flex-1 min-w-0 flex items-end bg-white border border-gray-300 rounded-full px-3 py-2 min-h-[40px]">
        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          onCompositionStart={() => { isComposingRef.current = true; }}
          onCompositionEnd={() => { isComposingRef.current = false; }}
          onFocus={onFocus}
          placeholder={disabled ? '送信できません' : 'メッセージを入力'}
          disabled={disabled}
          rows={1}
          className="w-full resize-none bg-transparent text-sm text-gray-800 placeholder-gray-400 focus:outline-none leading-[1.5] py-0 min-h-[22px] max-h-[88px] overflow-y-auto"
          style={{ scrollbarWidth: 'none' }}
        />
      </div>

      {/* 送信ボタン */}
      <button
        onClick={handleSend}
        disabled={!canSend}
        className={`flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap ${
          canSend
            ? 'bg-[#06C755] hover:bg-[#05b34a] active:scale-90 shadow-sm'
            : 'bg-gray-200'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        aria-label="送信"
      >
        <i className={`ri-send-plane-fill text-sm ${canSend ? 'text-white' : 'text-gray-400'}`}></i>
      </button>
    </div>
  );
}
